import React, { useState, useMemo } from 'react'
import { shareShop, getDirections, callShop } from '../lib/shareUtils'

/**
 * ShopList component displays a list of shops with their details
 * @param {Array} shops - Array of shop objects
 * @param {Function} onShopSelect - Function to call when a shop is selected
 */
const ShopList = ({ shops, onShopSelect, darkMode }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedPriceRange, setSelectedPriceRange] = useState('All')

  // Get unique categories
  const categories = useMemo(() => {
    const cats = shops.map(shop => shop.category)
    return ['All', ...new Set(cats)]
  }, [shops])

  // Filter shops based on search term, category, and price range
  const filteredShops = useMemo(() => {
    return shops.filter(shop => {
      const matchesSearch = shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           shop.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === 'All' || shop.category === selectedCategory
      const matchesPriceRange = selectedPriceRange === 'All' || shop.priceRange === selectedPriceRange
      return matchesSearch && matchesCategory && matchesPriceRange
    })
  }, [shops, searchTerm, selectedCategory, selectedPriceRange])

  return (
    <div className={`w-full md:w-full rounded-lg shadow-sm p-4 h-full flex flex-col overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Shops in Bandar Sri Indah</h2>
      
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search shops..."
            className={`w-full px-4 py-3 rounded-lg focus:ring-2 text-base ${darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'border border-gray-300 focus:ring-primary focus:border-primary'}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <svg
            className={`absolute right-3 top-3.5 h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Category Filter */}
      <div className="mb-4">
        <select
          className={`w-full px-4 py-3 rounded-lg focus:ring-2 text-base ${darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'border border-gray-300 focus:ring-primary focus:border-primary'}`}
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      {/* Price Range Filter */}
      <div className="mb-4">
        <select
          className={`w-full px-4 py-3 rounded-lg focus:ring-2 text-base ${darkMode ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'border border-gray-300 focus:ring-primary focus:border-primary'}`}
          value={selectedPriceRange}
          onChange={(e) => setSelectedPriceRange(e.target.value)}
        >
          <option value="All">All Price Ranges</option>
          <option value="$">Budget ($)</option>
          <option value="$$">Mid-range ($$)</option>
          <option value="$$$">Premium ($$$)</option>
        </select>
      </div>
      
      {/* Results count */}
      <div className={`mb-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {filteredShops.length} {filteredShops.length === 1 ? 'shop' : 'shops'} found
      </div>
      
      {/* Shop list container with flex-grow to take remaining space */}
      <div className="flex-grow overflow-y-auto min-h-0">
        {filteredShops.length === 0 ? (
          <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No shops found matching your search.</p>
        ) : (
          <div className="space-y-3">
            {filteredShops.map((shop) => (
              <div
                key={shop.id}
                className={`p-4 rounded-lg transition-all cursor-pointer border active:bg-primary/10 ${darkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-100 hover:bg-primary/5'}`}
                onClick={() => onShopSelect(shop)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className={`font-medium text-base ${darkMode ? 'text-white' : 'text-gray-800'}`}>{shop.name}</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{shop.category}</p>
                    <p className={`text-sm mt-1 line-clamp-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{shop.description}</p>
                    
                    {/* Rating */}
                    <div className="flex items-center mt-2">
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${i < Math.floor(shop.rating) ? 'text-yellow-400' : (darkMode ? 'text-gray-600' : 'text-gray-300')}`} viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className={`text-xs ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{shop.rating}</span>
                      <span className={`text-xs ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{shop.priceRange}</span>
                    </div>
                    
                    {/* Operating Hours */}
                    <div className={`flex items-center mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {shop.hours}
                    </div>
                    
                    {/* Promotions */}
                    {shop.promotion && (
                      <div className={`mt-2 text-xs px-2 py-1 rounded ${darkMode ? 'bg-red-900 text-red-200' : 'bg-red-50 text-red-600'}`}>
                        {shop.promotion}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-2 mt-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          getDirections(shop);
                        }}
                        className={`flex items-center text-xs px-2 py-1 rounded ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Directions
                      </button>
                      
                      {shop.phone && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            callShop(shop);
                          }}
                          className={`flex items-center text-xs px-2 py-1 rounded ${darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'} text-white`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          Call
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          shareShop(shop);
                        }}
                        className={`flex items-center text-xs px-2 py-1 rounded ${darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                        Share
                      </button>
                    </div>
                  </div>
                  
                  {/* Shop Image */}
                  {shop.image && (
                    <div className="ml-3 w-16 h-16 flex-shrink-0">
                      <img
                        src={shop.image}
                        alt={shop.name}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ShopList